import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store'
import { StripeAccountTypeEnum } from 'libs/gb-front/payment/data-access-stripe/src/lib/+state/gb-stripe.models'
import { CaptureAuctionFieldEnum, TippingComponentEnum } from './auction.models'
import * as fromAuction from './auction.reducer'
import * as AuthSelectors from 'libs/gb-front/auth/data-access/src/lib/+state/auth.selectors'
import { AUCTION_FEATURE_KEY } from './auction.reducer'
import { MenuItemTypeEnum } from './menu-items.models'

export interface RootState {
  auction: fromAuction.AuctionState
}

export const reducers: ActionReducerMap<RootState> = {
  auction: fromAuction.AuctionReducer,
}

export const selectAuctionState = createFeatureSelector<fromAuction.AuctionState>(AUCTION_FEATURE_KEY)

export const selectAuction = createSelector(selectAuctionState, (auctionState) => auctionState)

export const getAuctionLoaded = createSelector(selectAuction, (auctionState) => auctionState.loaded)

export const selectStripeConnect = createSelector(selectAuction, (auctionState) => auctionState.stripe_connect)

export const selectAuctionUrlName = createSelector(selectAuction, (auctionState) =>
  auctionState && auctionState.url_name ? auctionState.url_name : '',
)

export const selectItemCount = createSelector(selectAuction, (auctionState) => auctionState.item_count)

export const selectIsSingleItem = createSelector(selectAuction, (auctionState) => auctionState.is_single_item)

export const selectSingleItemUuid = createSelector(selectAuction, (auctionState) => auctionState.single_item_uuid)

export const selectIsSingleTicketPackage = createSelector(
  selectAuction,
  (auctionState) => auctionState.ticket_package_count === 1,
)

export const selectSingleTicketPackageUuid = createSelector(
  selectAuction,
  (auctionState) => auctionState.first_ticket_package_uuid,
)

export const selectAuctionStatus = createSelector(selectAuction, (auctionState) =>
  auctionState && auctionState.status ? auctionState.status : '',
)

export const selectAuctionLogo = createSelector(selectAuction, (auctionState) =>
  auctionState && auctionState.logo ? auctionState.logo : null,
)

export const selectAuctionTitle = createSelector(selectAuction, (auctionState) =>
  auctionState && auctionState.name ? auctionState.name : '',
)

export const selectAuctionCurrency = createSelector(selectAuction, (auctionState) =>
  auctionState && auctionState.currency ? auctionState.currency : '',
)

export const selectAuctionPaymentGateway = createSelector(selectAuction, (auctionState) =>
  auctionState && auctionState.payment_gateway ? auctionState.payment_gateway : '',
)

export const selectAuctionStripeConnectUuid = createSelector(selectAuction, (auctionState) =>
  auctionState && auctionState.stripe_connect ? auctionState.stripe_connect.uuid : '',
)

export const selectEnableActivityFeed = createSelector(
  selectAuction,
  (auctionState) => auctionState.enable_activity_feed,
)

export const selectEnableItemActivityFeed = createSelector(
  selectAuction,
  (auctionState) => auctionState.enable_item_activity_feed,
)

export const registrationFields = createSelector(selectAuction, (auctionState) => {
  return {
    capture_phone_numbers: auctionState.capture_phone_numbers,
    capture_table_numbers: auctionState.capture_table_numbers,
    capture_terms_and_conditions_agreement: auctionState.capture_terms_and_conditions_agreement,
    capture_addresses: auctionState.capture_addresses,
    capture_company_name: auctionState.capture_company_name,
    capture_company_address: auctionState.capture_company_address,
  }
})

export const termsAndConditions = createSelector(selectAuction, (auctionState) => auctionState.terms_and_conditions)

export const captureTermsAndConditions = createSelector(
  selectAuction,
  (auctionState) => auctionState.capture_terms_and_conditions_agreement,
)

export const capturePhoneNumbers = createSelector(selectAuction, (auctionState) => auctionState.capture_phone_numbers)

export const isSaas = createSelector(selectAuction, (auctionState) => auctionState.is_saas)

export const commissionEnabled = createSelector(selectAuction, (auction) => auction.commission_enabled)

export const tippingFeeEnabled = createSelector(selectAuction, (auction) => auction.tipping_fee_enabled)

export const tippingComponent = createSelector(selectAuction, (auction) => auction.tipping_component)

export const tippingFeeOptions = createSelector(selectAuction, (auction) =>
  // non-saas auctions should use tipping_fee_options (see GB-8473)
  auction.tipping_component === TippingComponentEnum.Slider && auction.is_saas
    ? auction.tipping_slider_amount_options
    : auction.tipping_fee_options,
)

export const defaultTippingFeeOption = createSelector(selectAuction, (auction) =>
  // non-saas auctions should use default_tipping_fee_option (see GB-8473)
  auction.tipping_component === TippingComponentEnum.Slider && auction.is_saas
    ? auction.default_tipping_slider_amount_option
    : auction.default_tipping_fee_option,
)

export const displaySocialLogin = createSelector(
  selectAuction,
  // TODO (GB-6543): Uncomment below once Facebook has been reactivated
  (auction) => !!auction.enable_google_login, // || !!auction.enable_facebook_login,
)

export const facebookLoginEnabled = createSelector(selectAuction, (auction) => auction.enable_facebook_login)

export const googleLoginEnabled = createSelector(selectAuction, (auction) => auction.enable_google_login)

export const stripeUserId = createSelector(selectAuction, (auction) => auction.stripe_user_id)

export const isStripeExpress = createSelector(
  selectAuction,
  (auction) => auction.stripe_connect?.account_type === StripeAccountTypeEnum.Express,
)

export const requiresAdditionalInfo = createSelector(selectAuction, (auction) => {
  if (!auction) {
    return false
  }
  if (auction.registration_custom_fields.length > 0 || auction.capture_terms_and_conditions_agreement) {
    return true
  }
  if (
    auction.capture_emails !== CaptureAuctionFieldEnum.Off &&
    auction.capture_phone_numbers !== CaptureAuctionFieldEnum.Off
  ) {
    return true
  }
  return false
})

export const giftAidEnabled = createSelector(selectAuction, (auctionState) => auctionState.gift_aid_enabled)

export const auctionScheduledEnd = createSelector(selectAuction, (auctionState) =>
  auctionState.scheduled && auctionState.scheduled_end ? auctionState.scheduled_end : null,
)

export const getAllCustomFields = createSelector(selectAuction, (auctionState) =>
  auctionState ? auctionState.registration_custom_fields : [],
)

export const getAllCategories = createSelector(selectAuction, (auctionState) =>
  auctionState ? auctionState.categories : [],
)

export const getHasCategoryWithItems = createSelector(getAllCategories, (categories) => categories.length > 0)

export const getTicketsLabel = createSelector(selectAuction, (auctionState) => auctionState.tickets_label)

export const getAllTaxes = createSelector(selectAuction, (auctionState) =>
  auctionState && auctionState.taxes && auctionState.taxes.length > 0 ? auctionState.taxes : [],
)

export const showTippingMessage = createSelector(selectAuction, (auctionState) => auctionState.showTippingMessage)

export const showEventTicketsPreview = createSelector(
  selectAuction,
  (auctionState) =>
    auctionState.enable_ticketing && auctionState.event_ticket_preview_enabled && auctionState.ticket_package_count > 0,
)

export const selectAuctionCountry = createSelector(selectAuction, (auctionState) => auctionState.country)

export const isEventTicketingEnabled = createSelector(selectAuction, (auctionState) => auctionState.enable_ticketing)

export const eventTicketsRemaining = createSelector(
  selectAuction,
  (auctionState) => auctionState.event_tickets_remaining,
)

export const getPasswordComplexityPolicy$ = createSelector(
  selectAuction,
  (auctionState) => auctionState.password_complexity_policy,
)

export const getAllPaymentMethods = createSelector(
  selectAuction,
  (auctionState) => auctionState && auctionState.payment_methods,
)

export const getPaymentMethodsByAuthProfile = createSelector(
  getAllPaymentMethods,
  AuthSelectors.selectIsVolunteer,
  (paymentMethods, isVolunteer) =>
    paymentMethods.filter((paymentMethod) =>
      isVolunteer ? paymentMethod.volunteer_payment_method : paymentMethod.guest_payment_method,
    ),
)

export const getSelectedPaymentMethod = createSelector(selectAuction, (auctionState) =>
  auctionState.selectedPaymentMethodId
    ? auctionState.payment_methods.find((paymentMethod) => paymentMethod.uuid === auctionState.selectedPaymentMethodId)
    : undefined,
)

export const applyFeeToFinalCharge = createSelector(getSelectedPaymentMethod, (selectedPaymentMethod) =>
  selectedPaymentMethod ? selectedPaymentMethod.apply_fee_to_final_charge : false,
)

export const paymentMethodFlatFee = createSelector(getSelectedPaymentMethod, (selectedPaymentMethod) =>
  selectedPaymentMethod ? selectedPaymentMethod.flat_fee : 0,
)

export const paymentMethodPercentageFee = createSelector(getSelectedPaymentMethod, (selectedPaymentMethod) =>
  selectedPaymentMethod ? selectedPaymentMethod.percentage_fee : 0,
)

export const getTaxByUuid = (uuid: string) =>
  createSelector(selectAuction, (auctionState) =>
    auctionState && auctionState.taxes && auctionState.taxes.length > 0
      ? auctionState.taxes.find((tax) => tax.uuid === uuid)
      : undefined,
  )

export const getMenuItemByType = (type: MenuItemTypeEnum) =>
  createSelector(selectAuction, (auctionState) => auctionState.menu_items.find((item) => item.button_type === type))

export const getMenuItemLabelByType = (type: MenuItemTypeEnum) =>
  createSelector(getMenuItemByType(type), (menuItem) => menuItem?.label)

export const getMenuItemIsVisible = (type: MenuItemTypeEnum) =>
  createSelector(getMenuItemByType(type), (menuItem) => menuItem?.is_visible)

export const selectAuctionMenuItems = createSelector(selectAuction, (auctionState) =>
  auctionState && auctionState.menu_items ? auctionState.menu_items : null,
)

export const paymentDonationComponent = createSelector(selectAuction, (auction) => auction.payment_donation_component)

export const paymentDonationSliderAmounts = createSelector(
  selectAuction,
  (auction) => auction.payment_donation_slider_amounts,
)

export const paymentDonationButtonAmounts = createSelector(
  selectAuction,
  (auction) => auction.payment_donation_button_amounts,
)
